import { generateTestText } from '@/components/CustomizationDrawer/generateTestText';
import type { Voice } from '@/features/tts/types';
import { createUnlockedAudioElement, loadAndPlayAudioBlob, releaseAudioElement } from './preserveAudioUserGesture';

const DEFAULT_TTS_ENDPOINT = '/api/speech/tts';

export interface PlayTtsPreviewAudioOptions {
  voice: Voice;
  text?: string;
  endpoint?: string;
  signal?: AbortSignal;
}

/**
 * Fetches a TTS preview clip and plays it via a blob URL on the same
 * gesture-unlocked audio element (required for iOS WebKit).
 */
export async function playTtsPreviewAudio({
  voice,
  text = generateTestText(),
  endpoint = DEFAULT_TTS_ENDPOINT,
  signal,
}: PlayTtsPreviewAudioOptions): Promise<HTMLAudioElement> {
  const audio = createUnlockedAudioElement();

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      body: JSON.stringify({ text, voice }),
      signal,
      responseErrorHandlers: {
        unknown: () => ({ shortCircuit: true }),
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch audio');
    }

    const blob = await response.blob();
    audio.addEventListener('ended', () => releaseAudioElement(audio), { once: true });
    await loadAndPlayAudioBlob(audio, blob);
    return audio;
  } catch (error) {
    releaseAudioElement(audio);
    throw error;
  }
}
